import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import superjson from 'superjson';
import type { TaskingBotChannelType, TaskingBotChannelStatus } from './schema';

export interface ChannelConfig {
  id: number;
  userId: number;
  channelType: TaskingBotChannelType;
  enabled: boolean;
  config: string; // JSON string
  webhookUrl: string | null;
  status: TaskingBotChannelStatus;
  lastError: string | null;
  lastActiveAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

const QUERY_KEY = ['taskingbot', 'channels'];

export function useTaskingBotChannels() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<ChannelConfig[]> => {
      const res = await fetch('/_api/taskingbot/channels', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch channels');
      const data = superjson.parse<{ channels: ChannelConfig[] }>(await res.text());
      return data.channels;
    },
    staleTime: 5000,
  });
}

export function useSaveChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      channelType: TaskingBotChannelType;
      enabled?: boolean;
      config?: Record<string, any>;
    }) => {
      console.log('[useSaveChannel] Sending request:', input);

      const res = await fetch('/_api/taskingbot/channels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify(input),
      });

      console.log('[useSaveChannel] Response status:', res.status);

      const responseText = await res.text();
      console.log('[useSaveChannel] Response body:', responseText);

      if (!res.ok) {
        let errorMessage = 'Failed to save channel';
        try {
          const errorData = superjson.parse<{ error: string }>(responseText);
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = responseText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = superjson.parse<{ channel: ChannelConfig }>(responseText);
      console.log('[useSaveChannel] Success:', result);
      return result;
    },
    onSuccess: () => {
      console.log('[useSaveChannel] Invalidating queries');
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error) => {
      console.error('[useSaveChannel] Error:', error);
    },
  });
}

export function useDeleteChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (channelType: TaskingBotChannelType) => {
      const res = await fetch('/_api/taskingbot/channels', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify({ channelType }),
      });
      if (!res.ok) throw new Error('Failed to delete channel');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

// Channel metadata - what config fields each channel needs
export const CHANNEL_META: Record<
  TaskingBotChannelType,
  {
    label: string;
    helpText: string;
    fields: { key: string; label: string; type: 'text' | 'password'; placeholder: string }[];
  }
> = {
  telegram: {
    label: 'Telegram',
    helpText: '1. Message @BotFather on Telegram\n2. Send /newbot and follow prompts\n3. Copy the Bot Token\n4. Add the bot to your group/channel\n5. Get the Chat ID (send a message, then visit api.telegram.org/bot<TOKEN>/getUpdates)',
    fields: [
      { key: 'botToken', label: 'Bot Token', type: 'password', placeholder: '123456:ABC-DEF...' },
      { key: 'chatId', label: 'Chat ID', type: 'text', placeholder: '-1001234567890 or @username' },
    ],
  },
  whatsapp: {
    label: 'WhatsApp',
    helpText: '1. Go to Meta Business Suite → developers.facebook.com\n2. Create a WhatsApp Business app\n3. Get your Phone Number ID and Access Token from the API Setup page\n4. Enter the recipient phone number with country code',
    fields: [
      { key: 'phoneNumberId', label: 'Phone Number ID', type: 'text', placeholder: '1234567890' },
      { key: 'accessToken', label: 'Access Token', type: 'password', placeholder: 'EAAx...' },
      {
        key: 'toPhone',
        label: 'Recipient Phone (with country code)',
        type: 'text',
        placeholder: '+12025551234',
      },
    ],
  },
  signal: {
    label: 'Signal',
    helpText: '1. Run signal-cli or signal-api as a REST service\n2. Register your phone number with Signal\n3. Enter the API URL where signal-cli is running\n4. Enter the phone number registered with Signal',
    fields: [
      { key: 'phoneNumber', label: 'Phone Number', type: 'text', placeholder: '+1234567890' },
      {
        key: 'apiUrl',
        label: 'Signal API URL',
        type: 'text',
        placeholder: 'http://localhost:8080',
      },
    ],
  },
  discord: {
    label: 'Discord',
    helpText: '1. Open Discord → Server Settings → Integrations\n2. Click Webhooks → New Webhook\n3. Choose the channel and customize the name\n4. Click Copy Webhook URL\n5. Paste it here',
    fields: [
      {
        key: 'webhookUrl',
        label: 'Webhook URL',
        type: 'password',
        placeholder: 'https://discord.com/api/webhooks/...',
      },
    ],
  },
  web: {
    label: 'Web Chat',
    helpText: 'Web Chat is built-in and always connected. No configuration needed.',
    fields: [],
  },
  voice: {
    label: 'Voice (Twilio)',
    helpText: '1. Sign up at twilio.com\n2. Get your Account SID and Auth Token from the Console\n3. Buy a phone number\n4. Set the webhook URL to your tasking.tech callback endpoint',
    fields: [
      { key: 'accountSid', label: 'Account SID', type: 'text', placeholder: 'AC...' },
      { key: 'authToken', label: 'Auth Token', type: 'password', placeholder: 'your auth token' },
      { key: 'fromNumber', label: 'Twilio Phone Number', type: 'text', placeholder: '+1234567890' },
      { key: 'toNumber', label: 'Your Phone Number', type: 'text', placeholder: '+1234567890' },
      {
        key: 'webhookBaseUrl',
        label: 'Webhook Base URL',
        type: 'text',
        placeholder: 'https://your-domain.com',
      },
    ],
  },
};
